import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { Container, MessageError, ContainerLoad, ContentInLine, SkeletonItem } from './styles'
import { ModalHeader } from '../ModalComponents/ModalHeader'
import { ModalInformations } from '../ModalComponents/ModalInformations'
import { HealthProcedureDetails } from '~/components/healthProcedures/healthProcedureDetails'
import { BirthProperty, EBirthReportCode, IBirthReport, IHealthProcedures } from '~/interfaces'
import { fetchBirthReport, message } from '~/services'
import { toFalse, toTrue } from '~/utils'
import { DataNotFound } from '~/components/shared'

interface Props {
  patientId: string
  healthProcedure: IHealthProcedures
  showModal: boolean
  setShowModal: Dispatch<SetStateAction<boolean>>
}

function sortBirthReportProperties(birthReportProperties: IBirthReport): BirthProperty[] {
  const priorityOrder = [
    EBirthReportCode.GESTATIONAL_AGE_AT_BIRTH_ID,
    EBirthReportCode.BIRTH_WEIGHT_ID,
    EBirthReportCode.BIRTH_HEIGHT_ID,
    EBirthReportCode.HEAD_CIRCUMFERENCE_ID,
    EBirthReportCode.CHEST_CIRCUMFERENCE_ID,
    EBirthReportCode.FETAL_POSITION_ID,
    EBirthReportCode.APGAR_1_MIN_ID,
    EBirthReportCode.APGAR_5_MIN_ID,
    EBirthReportCode.APGAR_10_MIN_ID,
    EBirthReportCode.NEWBORN_COMPLICATIONS_ID,
    undefined
  ]

  return birthReportProperties.properties.sort(
    (x: BirthProperty, y: BirthProperty) => priorityOrder.indexOf(x.code) - priorityOrder.indexOf(y.code)
  )
}

export const BirthReportDetails = ({ healthProcedure, patientId, setShowModal, showModal }: Props): JSX.Element => {
  const [birthReport, setBirthReport] = useState<BirthProperty[] | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const renderContent = () => {
    if (isLoading) {
      return (
        <ContainerLoad data-testId="spinnerComponent">
          <SkeletonItem width={'100%'} height={'24px'} />
          <ContentInLine>
            <SkeletonItem width={'116px'} height={'24px'} />
            <SkeletonItem width={'204px'} height={'24px'} />
          </ContentInLine>
          <ContentInLine>
            <SkeletonItem width={'204px'} height={'24px'} />
            <SkeletonItem width={'116px'} height={'24px'} />
          </ContentInLine>
        </ContainerLoad>
      )
    }
    if (errorMessage) {
      return (
        <MessageError>
          <DataNotFound text={errorMessage} />
        </MessageError>
      )
    }
    return (
      <>
        <ModalHeader title={healthProcedure.description} datetime={healthProcedure.startDate} />
        {birthReport?.map(({ name, result }: BirthProperty) => (
          <ModalInformations key={name} label={name} value={result} />
        ))}
      </>
    )
  }

  const handleBirthReportDetailsErrors = (error: any) => {
    if (error?.status === '404') {
      return setErrorMessage(message('messages.birthReportNotFound'))
    }
    return setErrorMessage(message('messages.genericUnexpectedError'))
  }

  const receiveBirthReport = (birthReport: IBirthReport) => {
    const orderedBirthProperties = sortBirthReportProperties(birthReport)
    setBirthReport(orderedBirthProperties)
  }

  useEffect(() => {
    setIsLoading(toTrue)
    fetchBirthReport({ pathParams: { patientId: healthProcedure.id } })
      .then(receiveBirthReport)
      .catch(handleBirthReportDetailsErrors)
      .finally(() => setIsLoading(toFalse))
  }, [patientId, healthProcedure.id])

  return (
    <HealthProcedureDetails showModal={showModal} setShowModal={setShowModal} viewType="SideModal">
      <Container>{renderContent()}</Container>
    </HealthProcedureDetails>
  )
}
